import React, { useContext } from "react";
import { observer } from "mobx-react-lite";
import pl from "shared/messages/pl";
import { constants } from "shared/constants";
import { SalesPerson } from "view360Card/models";
import { EntityDetailsItem, PhoneNumber } from "view360Card/components";
import { getValuesOrDefaultRowView, mapBoolValues } from "utils/entityUtils";
import { TrainingsTable } from "../trainingsTable/TrainingsTable";
import { OfwcaProfilesTable } from "../ofwcaProfilesTable/OfwcaProfilesTable";
import {
    DynamiAttributesSection,
    getDynamicParameterAttrValue,
} from "../dynamicAttributesSection/DynamiAttributesSection";
import { formatDate } from "utils/formattingUtils";
import { Flex } from "@fluentui/react-northstar";
import { MsTeamsContext } from "msTeams/store";

export interface GeneralInformationSalesPersonProps {
    salesPerson: SalesPerson;
}

export const GeneralInformationSalesPerson: React.FC<GeneralInformationSalesPersonProps> = observer((props) => {
    const msTeamsContext = useContext(MsTeamsContext);

    const getTrainingTable = () => {
        if (props.salesPerson.trainingList.length === 0) {
            return null;
        }
        return <TrainingsTable trainings={props.salesPerson.trainingList} />;
    };

    const getOfwcaProfileTable = () => {
        if (props.salesPerson.profileList.length === 0) {
            return null;
        }
        return <OfwcaProfilesTable profiles={props.salesPerson.profileList} />;
    };

    const getPhoneNumbers = () => {
        return (
            <Flex column={msTeamsContext.isMobileClient} gap="gap.small">
                {props.salesPerson.phoneNumberEHM ? (
                    <PhoneNumber number={props.salesPerson.phoneNumberEHM} text="EHM" />
                ) : (
                    constants.noValue
                )}
                {!msTeamsContext.isMobileClient && <div>&nbsp;{constants.inlineSeparator}&nbsp;</div>}
                {props.salesPerson.phoneNumberEHZ ? (
                    <PhoneNumber number={props.salesPerson.phoneNumberEHZ} text="EHŻ" />
                ) : (
                    constants.noValue
                )}
            </Flex>
        );
    };

    const getinfoPortalRegistration = () => {
        return getValuesOrDefaultRowView([
            getDynamicParameterValue(constants.dynamicAttributeCodes.infoportalRegistratorionEhm),
            getDynamicParameterValue(constants.dynamicAttributeCodes.infoportalRegistratorionEhz),
        ]);
    };

    const getDynamicParameterValue = (code: string) => {
        const dynamiAttribute = props.salesPerson?.dynamicAttributeList?.find((p) => p.code === code);
        if (!dynamiAttribute) {
            return undefined;
        }

        return getDynamicParameterAttrValue(dynamiAttribute);
    };

    const getCoins = () => {
        return getValuesOrDefaultRowView([
            getDynamicParameterValue(constants.dynamicAttributeCodes.ipunktCoinsTotal),
            getDynamicParameterValue(constants.dynamicAttributeCodes.ipunktCoinsPaid),
        ]);
    };

    return (
        <>
            <EntityDetailsItem
                label={pl.view360Card.generalInformations.salesPerson.punuId}
                content={getValuesOrDefaultRowView([props.salesPerson.punu])}
            />
            <EntityDetailsItem
                label={pl.view360Card.generalInformations.salesPerson.status}
                content={getValuesOrDefaultRowView([props.salesPerson.status?.description])}
            />
            <EntityDetailsItem
                label={pl.view360Card.generalInformations.salesPerson.birthDate}
                content={getValuesOrDefaultRowView([formatDate(props.salesPerson.birthDate)])}
            />
            <EntityDetailsItem
                label={pl.view360Card.generalInformations.salesPerson.systemLogin}
                content={getValuesOrDefaultRowView([props.salesPerson.systemNumber])}
            />
            <EntityDetailsItem
                label={pl.view360Card.generalInformations.salesPerson.infoPortalRegistration}
                content={getinfoPortalRegistration()}
            />
            <EntityDetailsItem
                label={pl.view360Card.generalInformations.salesPerson.phoneNo}
                content={getPhoneNumbers()}
            />
            <EntityDetailsItem
                label={pl.view360Card.generalInformations.salesPerson.emailAddress}
                content={getValuesOrDefaultRowView([props.salesPerson.emailEHM, props.salesPerson.emailEHZ])}
            />
            <EntityDetailsItem
                label={pl.view360Card.generalInformations.salesPerson.marketingConsents}
                content={getValuesOrDefaultRowView([
                    mapBoolValues(props.salesPerson.marketingConsentEHM),
                    mapBoolValues(props.salesPerson.marketingConsentEHZ),
                ])}
            />
            <EntityDetailsItem
                label={pl.view360Card.generalInformations.salesPerson.knf}
                content={getValuesOrDefaultRowView([
                    formatDate(props.salesPerson.knfDateEHM),
                    formatDate(props.salesPerson.knfDateEHZ),
                ])}
            />
            <EntityDetailsItem
                label={pl.view360Card.generalInformations.salesPerson.iPunktUserStatus}
                content={getValuesOrDefaultRowView([props.salesPerson.ipunktStatus])}
            />
            <EntityDetailsItem label={pl.view360Card.generalInformations.salesPerson.coins} content={getCoins()} />
            <EntityDetailsItem
                label={pl.view360Card.generalInformations.salesPerson.certification}
                content={getValuesOrDefaultRowView([props.salesPerson.certification?.description])}
            />
            <DynamiAttributesSection
                attributes={props.salesPerson.dynamicAttributeList}
                attributeCodesToIgnore={[
                    constants.dynamicAttributeCodes.infoportalRegistratorionEhm,
                    constants.dynamicAttributeCodes.infoportalRegistratorionEhz,
                    constants.dynamicAttributeCodes.ipunktCoinsPaid,
                    constants.dynamicAttributeCodes.ipunktCoinsTotal,
                ]}
            />
            <EntityDetailsItem
                label={pl.view360Card.generalInformations.salesPerson.ofwcaProfile.header}
                content={getOfwcaProfileTable()}
                wrap={props.salesPerson.profileList.length > 0}
            />
            <EntityDetailsItem
                label={pl.view360Card.generalInformations.salesPerson.training.header}
                content={getTrainingTable()}
                wrap={props.salesPerson.trainingList.length > 0}
            />
        </>
    );
});
